import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import * as IBooks from '../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../interfaces/merchants/resources/books/inputs';

import scrollToTop from '../../../hoc/scrollToTop';

import {
  inputBookingTargets,
  RestartReservationError,
} from '../../../../redux/actions/merchants/resources/books/inputs';
import { Dispatch } from '../../../../redux/reducers/merchants/resources/books/inputs';

import BooksSubmitButton from '../../../../components/books/common/buttons/BooksSubmitButton';
import BooksLabeledContainer from '../../../../components/books/model1/BooksLabeledContainer';
import InputBookingTargetCourseSelector from './InputBookingTargetCourseSelector';
import InputBookingTargetOptionSelector from './InputBookingTargetOptionSelector';
import Preview from './InputBookingTargetPreview';

interface ElementProps {
  modelType: IBooks.ModelType;
  settings: IBooks.SettingBookingTargets;
  onSubmit: () => void;
  onRestart: () => void;
  renew?: boolean;
  postable: boolean;
  withExpressFlow: boolean;
}

interface StateProps {
  inputs: IInputs.State;
}

interface DispatchProps {
  actions: {
    handleSubmit: (
      modelType: IBooks.ModelType,
      merchantId: string,
      resourceId: string,
      course: IBooks.Course,
      withExpressFlow: boolean,
      options?: IBooks.Option[],
      reservationId?: number,
    ) => Promise<void>;
  };
}

interface State {
  course?: IBooks.Course;
  options?: IBooks.Option[];
}

type Props = ElementProps & StateProps & DispatchProps & InjectedIntlProps;

class InputBookingTarget extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const {
      inputs: { course, options },
      renew,
    } = props;
    this.state = {
      course: renew ? undefined : course,
      options: renew ? [] : options || [],
    };
    this.handleClickCourse = this.handleClickCourse.bind(this);
    this.handleClickOption = this.handleClickOption.bind(this);
    this.handleChangeTarget = this.handleChangeTarget.bind(this);
    this._handleSubmit = this._handleSubmit.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  public componentDidMount() {
    const {
      inputs: { reservationId },
      settings: { selectedCourse },
    } = this.props;
    // exclude target reselect.
    if (!reservationId && selectedCourse) {
      this.handleClickCourse(selectedCourse);
    }
  }

  private handleClickCourse(course: IBooks.Course) {
    if (course.options.length === 0) {
      // 選択されたコースにオプションが存在しなければオプション選択はskip
      this._handleSubmit({ course, options: [] });
      return;
    }
    this.setState({ ...this.state, course });
  }

  private handleClickOption(options: IBooks.Option[]) {
    this.setState({ ...this.state, options });
  }

  private async handleSubmit() {
    this._handleSubmit(this.state);
  }

  private async _handleSubmit({ course, options }: State) {
    const {
      modelType,
      onSubmit,
      onRestart,
      inputs: { merchantId, resourceId, reservationId },
      withExpressFlow,
    } = this.props;
    try {
      await this.props.actions.handleSubmit(
        modelType,
        merchantId!,
        resourceId!,
        course!,
        withExpressFlow,
        options,
        reservationId,
      );
      onSubmit();
    } catch (e) {
      if (e instanceof RestartReservationError) {
        onRestart();
      }
    }
  }

  private handleChangeTarget(targets: { course: boolean; options: boolean }) {
    const { course, options } = targets;
    if (!course && options) {
      throw new Error(
        'Selected Options should not be able to change in this component.',
      );
    }
    if (course) {
      this.setState({ course: undefined, options: [] });
    }
  }

  public render() {
    const { settings, intl, postable } = this.props;
    const { course, options } = this.state;

    return (
      <>
        {!course || course.options.length === 0 ? (
          <InputBookingTargetCourseSelector
            courses={settings.courses}
            postable={postable}
            onClick={this.handleClickCourse}
          />
        ) : (
          <div>
            <Preview
              course={course}
              options={[]} // 選択中のoptionは表示しない
              onChangeTarget={this.handleChangeTarget}
            />
            <BooksLabeledContainer
              label={intl.formatMessage({
                id: 'books.formsLabel.addOptions',
              })}
              paddingTop={true}
            >
              <InputBookingTargetOptionSelector
                options={course.options}
                checkedOptions={options || []}
                onClick={this.handleClickOption}
              />
            </BooksLabeledContainer>
            <BooksSubmitButton
              type="button"
              onClick={this.handleSubmit}
              disabled={!postable}
              postable={postable}
              value={intl.formatMessage({
                id: 'books.submitLabel.selectDateTime',
              })}
            >
              {intl.formatMessage({
                id: 'books.submitLabel.selectDateTime',
              })}
            </BooksSubmitButton>
          </div>
        )}
      </>
    );
  }
}

const mapStateProps = ({
  merchantsResourcesBooksInputs,
}: {
  merchantsResourcesBooksInputs: IInputs.State;
}): StateProps => ({
  inputs: merchantsResourcesBooksInputs,
});

const randomStaff: IBooks.RandomStaff = { id: '' };

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    handleSubmit: (
      modelType: IBooks.ModelType,
      merchantId: string,
      resourceId: string,
      course: IBooks.Course,
      withExpressFlow: boolean,
      options?: IBooks.Option[],
      reservationId?: number,
    ) =>
      inputBookingTargets(
        modelType,
        merchantId,
        resourceId,
        { course, options, staff: randomStaff },
        withExpressFlow,
        reservationId,
      )(dispatch),
  },
});

export default connect(
  mapStateProps,
  mapDispatchToProps,
)(injectIntl(scrollToTop(InputBookingTarget)));
